<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useGtm } from '@gtm-support/vue-gtm'
import { ParagraphEventRegistration, TaxonomyTermEducationalCourse } from '~/api/graphql/generated'
import { generateIcsUrl, sha256, normalizeEmail, normalizePhone } from '~/lib/utils'
import sanitizeHtml from 'sanitize-html'
import { FormKitMessages } from '@formkit/vue'
import { Option } from '~/components/atoms/forms/select/Select.vue'
import { useRecaptcha } from '~/api/drupal/useRecaptcha'

const props = defineProps<{
  entity: ParagraphEventRegistration
}>()

const { t } = useI18n()
const { generateToken, verifyToken } = useRecaptcha()
const config = useRuntimeConfig()
const gtm = useGtm()

const submitted = ref(false)
const successMessage = ref(props.entity?.fieldSuccessMessage?.processed ?? t('form.eventRegistrationForm.success'))

const formErrors = ref()

const icsTitle = ref(props.entity?.fieldIcsTitle)
const icsStartDate = ref(props.entity?.fieldIcsStartDateTime?.date)
const icsEndDate = ref(props.entity?.fieldIcsEndDateTime?.date)
const icsLocation = ref(props.entity?.fieldIcsLocation)

const icsUrl = generateIcsUrl(icsStartDate.value, icsEndDate.value, icsTitle.value ?? '', icsLocation.value ?? '')

const eventDate = computed(() => props.entity?.fieldDate?.date)
const eventType = computed(() => props.entity?.fieldEventType)
const listId = computed(() => props.entity?.fieldListId)
const dateTime = ref(props.entity?.fieldStartTime)
const location = ref(props.entity?.fieldLocation)

const educationalCourseOptions = computed(() =>
  props.entity?.fieldEducationalCourseOptions?.map((term) => {
    const entity = term?.entity as TaxonomyTermEducationalCourse
    return {
      label: entity?.name ?? '',
      value: entity?.fieldExternalId ? entity.fieldExternalId : (String(entity.tid) ?? ''),
    }
  }),
)

const referenceOptions = [...Array(14).keys()].map((item, index: number): Option => {
  return {
    value: index + 1,
    label: t(`form.eventRegistrationForm.referenceOptions.${index + 1}`),
  }
})

const referenceOptionValue = ref<number>()
const showReferenceOther = computed(() => referenceOptionValue.value === 14)

const salutationOptions = [...Array(4).keys()].map((item, index: number): Option => {
  return {
    value: index + 1,
    label: t(`form.eventRegistrationForm.salutationOptions.${index + 1}`),
  }
})

const showCrossSellingCheckbox = computed(() => {
  return props.entity.fieldShowCrossSelling || false
})

const crossSellingContactAgreement = ref(false)

const eventRegistrationSuccess = ref(null)
const scrollToTop = () => {
  if (import.meta.client && eventRegistrationSuccess.value) {
    const element = eventRegistrationSuccess.value as HTMLElement

    const top = element.getBoundingClientRect().top + document.documentElement.scrollTop

    window.scrollTo({
      top: top - 50,
      behavior: 'smooth',
    })
  }
}

interface EventRegistrationFormData {
  'personal-details': {
    email: string
    first_name: string
    last_name: string
    salutation: string
    phone: string
    birthdate: string
    newsletter: boolean
  }
  'reference-group': {
    referenceOption: string
    other?: string
  }
  'course-group': {
    course: string
  }
}

const handleSubmit = async (data: EventRegistrationFormData) => {
  formErrors.value = []

  const token = await generateToken('sectionFormRecaptcha')
  if (!token) {
    formErrors.value = [t('form.errors.generalError')]
    return
  }

  const recaptchaResult = await verifyToken(token)
  if (!recaptchaResult) {
    formErrors.value = [t('form.errors.generalError')]
    return
  }

  const currentDate = new Date()
  const eventDateArray = eventDate.value.split(' ')

  const personalDetails = data['personal-details']

  const payload = {
    email: personalDetails.email,
    attributes: {
      VORNAME: personalDetails.first_name,
      NACHNAME: personalDetails.last_name,
      ANREDE: parseInt(personalDetails.salutation),
      // Replace spaces with empty string to match the format of the phone number in the Brevo API.
      SMS: personalDetails.phone.replace(/\s/g, ''),
      AUFMERKSAM: parseInt(data['reference-group'].referenceOption),
      ANDERE: data['reference-group'].other ?? '',
      GEBURTSDATUM: personalDetails.birthdate,
      BILDUNGSGANG: parseInt(data['course-group'].course),
      NEWSLETTER_CHECKED: personalDetails.newsletter,
      OPT_IN_DATUM_ANMELDUNG_VIA_FORMULAR: currentDate.toISOString().split('T')[0],
      DURCHFUERHUNG_VERANSTALTUNG: parseInt(eventType.value ?? ''),
      DATUM_VERANSTALTUNG: eventDateArray.length > 0 ? eventDateArray[0] : '',
      OPT_IN: true,
      'DOUBLE_OPT-IN': 2,
      TAG_DATUM_UHRZEIT_ART_VERANSTALTUNG: dateTime.value,
      REGISTIERUNG_INFOVERANSTALTUNG: currentDate.toISOString().split('T')[0],
      CROSS_SELLING_KONTAKT: crossSellingContactAgreement.value,
    },
    emailBlacklisted: false,
    smsBlacklisted: false,
    listIds: [parseInt(listId.value ?? '')],
    updateEnabled: true,
  }

  try {
    const result = await fetch(`${config.public.frontendBaseUrl}/api/brevo/event-registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    const data = await result.json()

    scrollToTop()

    if (data.success) {
      submitted.value = true

      // Prepare hashed email and phone for GTM tracking
      const normalizedEmail = normalizeEmail(personalDetails.email)
      const normalizedPhone = normalizePhone(personalDetails.phone || '')

      const hashedEmail = await sha256(normalizedEmail)
      const hashedPhone = personalDetails.phone ? await sha256(normalizedPhone) : undefined

      // Build GTM tracking event with hashed email and phone
      const gtmEvent: Record<string, string | undefined> = {
        event: 'cbz_infoevent_registration',
        'Anmeldung Infoevent': dateTime.value ?? undefined,
        email: hashedEmail,
      }

      // Only add phone if it exists
      if (hashedPhone) {
        gtmEvent.phone = hashedPhone
      }

      gtm?.trackEvent(gtmEvent)
    }

    if (data.message) {
      formErrors.value.push(data.message)
    }
  } catch (e) {
    console.log(e)
  }

  if (personalDetails.newsletter) {
    const newsletterPayload = {
      email: personalDetails.email,
      attributes: {
        VORNAME: personalDetails.first_name,
        NACHNAME: personalDetails.last_name,
        ANREDE: parseInt(personalDetails.salutation),
        OPT_IN_DATUM_ANMELDUNG_VIA_FORMULAR: currentDate.toISOString().split('T')[0],
        OPT_IN: false,
      },
      includeListIds: [parseInt(config.public.newsletterListId) || 11],
      templateId: 7, // Hardcoded as per CARW-387.
      redirectionUrl: 'https://careum.ch/bestaetigung-newsletter-careum-bildungszentrum',
    }

    try {
      const result = await fetch(`${config.public.frontendBaseUrl}/api/brevo/newsletter-subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newsletterPayload),
      })

      const data = await result.json()

      if (!data) {
        console.log('Something went wrong with newsletter subscription!')
      }
    } catch (e) {
      console.log(e)
    }
  }
}
</script>

<template>
<FormKitLazyProvider config-file="true">

  <div ref="eventRegistrationSuccess" />
  <div v-if="submitted" class="event-registration-success">
    <div class="message" v-html="successMessage" />

    <Button
      v-if="icsTitle && icsStartDate && icsEndDate"
      secondary
      icon="download"
      :to="icsUrl"
      :mime-type="`text/calendar`"
    >
      {{ t('form.eventRegistrationForm.downloadIcs') }}
    </Button>
  </div>

  <FormKit v-if="!submitted" type="form" :actions="false" @submit="handleSubmit">
    <div class="form-section form-header">
      <span class="form-header-helper">
        {{ t('form.eventRegistrationForm.sectionHelper') }}
      </span>
      <h4>{{ t('form.eventRegistrationForm.eventInfoTextLabel') }}</h4>
      <div class="form-header-subtitle">
        {{ dateTime }}
        <br v-if="location" />
        {{ location }}
      </div>
    </div>

    <div class="separator" />

    <FormKit v-if="educationalCourseOptions" type="group" name="course-group">
      <h4>
        {{ t('form.eventRegistrationForm.educationalCourse') }}
      </h4>
      <div class="form-section course-radios">
        <Radio id="event-registration-course" name="course" :options="educationalCourseOptions" :required="false" />
      </div>
    </FormKit>

    <div class="separator" />

    <FormKit v-if="referenceOptions" type="group" name="reference-group">
      <h4>{{ t('form.eventRegistrationForm.reference') }}</h4>
      <div class="form-section">
        <Select
          id="event-registration-reference-option"
          v-model="referenceOptionValue"
          name="referenceOption"
          :label="t('form.eventRegistrationForm.referencePlaceholder')"
          :options="referenceOptions"
          :required="true"
        />

        <Input
          v-if="showReferenceOther"
          id="event-registration-other"
          name="other"
          :required="true"
          :label="t('form.eventRegistrationForm.other')"
        />
      </div>
    </FormKit>

    <div class="separator" />

    <FormKit type="group" name="personal-details">
      <h4>
        {{ t('form.eventRegistrationForm.personalDetails') }}
      </h4>

      <div class="form-section">
        <Select
          id="event-registration-salutation"
          name="salutation"
          :options="salutationOptions"
          :label="t('form.eventRegistrationForm.salutation')"
          :required="true"
        />

        <div class="form-section--2-cols">
          <Input
            id="event-registration-first-name"
            name="first_name"
            :label="t('form.eventRegistrationForm.firstName')"
            :required="true"
          />
          <Input
            id="event-registration-last-name"
            name="last_name"
            :label="t('form.eventRegistrationForm.lastName')"
            :required="true"
          />
        </div>

        <div class="form-section--2-cols">
          <Email
            id="event-registration-email"
            name="email"
            :label="t('form.eventRegistrationForm.email')"
            :required="true"
          />
          <TelInput
            id="event-registration-phone"
            name="phone"
            :label="t('form.eventRegistrationForm.phone')"
            :required="false"
          />
        </div>

        <Date
          id="event-registration-birthdate"
          name="birthdate"
          :required="true"
          :label="t('form.eventRegistrationForm.birthdate')"
          :before="new Date()"
        />

        <Checkbox
          id="event-registration-terms"
          name="terms"
          :label="sanitizeHtml(props.entity?.fieldTerms?.processed ?? '')"
          :html-label="true"
          :required="true"
          :validation-messages="{
            accepted: t('form.termsAndConditions.validationMessage'),
          }"
        />

        <Checkbox
          id="event-registration-newsletter"
          name="newsletter"
          :label="t('form.eventRegistrationForm.newsletterOption')"
          :required="false"
        />

        <Checkbox
          v-show="showCrossSellingCheckbox"
          id="event-registration-cross-selling"
          v-model="crossSellingContactAgreement"
          name="crossSellingContactAgreement"
          :label="t('form.eventRegistrationForm.crossSellingContactAgreement')"
          :html-label="true"
          :required="false"
        />
      </div>
    </FormKit>

    <RecaptchaInfo />

    <div class="buttons-wrapper">
      <FormKitMessages />

      <Button primary slide class="submit-button">
        {{ t('form.eventRegistrationForm.submit') }}
      </Button>
    </div>
  </FormKit>

</FormKitLazyProvider>
</template>

<style lang="scss" scoped>
.form-header {
  font-size: 14px;

  .form-header-helper {
    color: var(--c-blue-100);
  }

  .form-header-subtitle {
    line-height: 22px;
  }
}
.course-radios {
  :deep(.formkit-options) {
    flex-direction: column;
  }
}

.buttons-wrapper {
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 30px;

  @include breakpoint(tl) {
    margin-top: 80px;

    justify-content: flex-end;
    flex-direction: row;
  }

  .formkit-messages {
    margin-right: auto;

    display: flex;
    justify-content: center;
  }
}
</style>
